import "./App.css";

import { useState, useEffect, memo } from "react";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Icon } from "@iconify/react";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

import CommentIcon from "@mui/icons-material/Comment";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";
import LinkIcon from "@mui/icons-material/Link";
import Popper from "@mui/material/Popper";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import RepeatIcon from "@mui/icons-material/Repeat";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import { useRecoilValue } from "recoil";
import PaletteIcon from "@mui/icons-material/Palette";
import FormatColorResetIcon from "@mui/icons-material/FormatColorReset";
import Slider from "@mui/material/Slider";

import { http_post, activeProject, setMessageProps } from "./Auth";

import { debugToggleState } from "./RecoilStates";
import {
  FONT_SIZE,
  ICON_FONT_SIZE,
  MIN_FONT_SIZE,
  MAX_FONT_SIZE,
  DEFAULT_FONT_SIZE,
} from "./constants";

import {
  isGithub,
  isGitlab,
  UserName,
  getTimestamp,
  getUser,
  getSourceUrl,
} from "./MessagePreview";

import PouchDB from "pouchdb-browser";
import {
  Wheel,
  hsvaToHex,
  hexToHsva,
  getContrastingColor,
} from "@uiw/react-color";
import Swatch from "@uiw/react-color-swatch";

import MessagePreview from "./MessagePreview";
import * as u from "./utility";

function Point({ color, checked }) {
  if (!checked) return null;
  return (
    <div
      style={{
        height: 5,
        width: 5,
        borderRadius: "50%",
        backgroundColor: getContrastingColor(color),
      }}
    />
  );
}

function ColorPicker({ color, onChange, fontSize, onChangeFontSize }) {
  const [hsva, setHsva] = useState(hexToHsva(color));
  const [anchorEl, setAnchorEl] = useState(null);
  const [sliderPos, setSliderPos] = useState(fontSize);

  const handleClick = (e) => {
    setAnchorEl(anchorEl ? null : e.currentTarget);
  };

  const handleColorChange = (color) => {
    const res = { ...hsva, ...color.hsva };
    setHsva(res);

    const hex = hsvaToHex(res);
    onChange(hex);
  };

  const handleResetColor = () => {
    handleColorChange({ hsva: hexToHsva("#ffffff") });
  };

  const handleSlider = (e, v) => {
    setSliderPos(v);
    onChangeFontSize(v);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        id={anchorEl ? "color-picker-popper" : undefined}
      >
        <PaletteIcon sx={{ color: "silver", fontSize: ICON_FONT_SIZE }} />
      </IconButton>
      <Popper
        id={anchorEl ? "color-picker-popper" : undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
      >
        <Box
          sx={{
            backgroundColor: "#606060",
            borderRadius: "5px",
            p: 1,
            m: 1,
            mr: 3,
            ml: 3,
            opacity: 0.95,
            position: "relative",
            display: "inline-block",
          }}
        >
          <Box sx={{ position: "absolute", right: 0.4, top: 0.4, zIndex: 5 }}>
            <IconButton size="small" onClick={handleResetColor}>
              <FormatColorResetIcon
                sx={{ color: "silver", fontSize: ICON_FONT_SIZE }}
              />
            </IconButton>
          </Box>
          <Stack direction="row" gap={1} alignItems={"center"}>
            <Box sx={{ height: 100 }}>
              <Slider
                aria-label="Font Size"
                orientation="vertical"
                value={sliderPos}
                onChange={handleSlider}
                sx={{ color: "darkgrey" }}
                min={MIN_FONT_SIZE}
                max={MAX_FONT_SIZE}
                step={1}
                valueLabelDisplay="auto"
              />
            </Box>
            <Stack direction="column" alignItems={"center"} gap={1}>
              <Wheel
                color={hsva}
                onChange={handleColorChange}
                width={100}
                height={100}
              />

              <Swatch
                colors={["#f4b5de", "#fed2bf", "#fcf9a6", "#badfb9", "#bbc0df"]}
                color={hsvaToHex(hsva)}
                rectProps={{
                  children: <Point />,
                  style: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                }}
                onChange={(hsva) => handleColorChange({ hsva: hsva })}
              />
            </Stack>
          </Stack>
        </Box>
      </Popper>
    </>
  );
}

export function ThreadRow({
  item,
  sx,
  nodebug,
  expand,
  overflow,
  storyKey,
  disjoin,
  setMessages,
  story,
  allowReplay,
}) {
  const [rawJson, setRawJson] = useState(false);
  const [fontSize, setFontSize] = useState(
    (item.st_ui && item.st_ui.font_size) || FONT_SIZE[0]
  );
  const [hover, setHover] = useState(false);
  const [color, setColor] = useState(
    (item.st_ui && item.st_ui.bgcolor) || "#ffffff"
  );

  const handleReplay = (item) => {
    const replay = async () => {
      try {
        http_post("label", { replaylog: { events: [item] } });
      } catch (err) {
      } finally {
      }
    };

    replay();
  };

  const handleDisjoinMessage = (item) => {
    const request = async () => {
      try {
        const data = { disjoin: { thread: item.PK, message: item.SK } };
        http_post("label", data);
      } catch (err) {}
    };

    setMessages((prev) => {
      let index = prev.findIndex((e) => e.SK === item.SK);

      if (index !== -1) {
        request();
        const result = Array.from(prev);
        result.splice(index, 1);
        return result;
      }

      return prev;
    });
  };

  const handleDisjoinThread = (item) => {
    const request = async (threadId) => {
      try {
        http_post("label", { disjoin: { story: story.SK, thread: threadId } });
      } catch (err) {
      } finally {
      }
    };

    const threadId = item.PK.split("#").slice(1).join("#");
    request(threadId);

    setMessages((prev) => {
      return prev.filter((e) => e.PK !== item.PK);
    });
  };

  const removeThreadFromCache = () => {
    if (story) {
      if (activeProject) {
        const db = new PouchDB(activeProject);
        const key = `Thread:${story.PK}:${story.SK}`;
        db.get(key)
          .then((doc) => {
            db.remove(doc).catch((e) => {});
          })
          .catch((err) => {});
      }
    }
  };

  const handleCloseMessage = (item) => {
    setMessages((prev) => {
      let newMessages = [...prev];
      let found = newMessages.find((m) => m.SK === item.SK && m.PK === item.PK);
      if (found) {
        found.st_hide = true;
      }
      return newMessages;
    });

    removeThreadFromCache();

    setMessageProps(item, "st_hide", true);
  };

  const handleRestoreMessage = (item) => {
    setMessages((prev) => {
      let newMessages = [...prev];
      let found = newMessages.find((m) => m.SK === item.SK && m.PK === item.PK);
      if (found) {
        found.st_hide = false;
      }
      return newMessages;
    });

    removeThreadFromCache();
    setMessageProps(item, "st_hide", false);
  };

  const toggleRawJson = () => {
    setRawJson((prev) => !prev);
  };

  const getMessageJson = () => {
    const messageJsonStr = JSON.stringify(item, null, 2);
    return messageJsonStr;
  };

  const commitAppearence = (key, value) => {
    setMessageProps(item, `st_ui/${key}`, value);

    setMessages((prev) => {
      let index = prev.findIndex((e) => e.SK === item.SK);

      if (index !== -1) {
        const next = [...prev];
        if (!next[index].st_ui) {
          next[index].st_ui = {};
        }
        next[index].st_ui[key] = value;
        return next;
      }

      return prev;
    });

    removeThreadFromCache();
  };

  const handleFontChange = (newSize) => {
    setFontSize(newSize);

    commitAppearence(
      "font_size",
      newSize === DEFAULT_FONT_SIZE ? null : newSize
    );
  };

  const handleHighlight = () => {
    const index =
      (FONT_SIZE.findIndex((e) => e === fontSize) + 1) % FONT_SIZE.length;
    const newSize = FONT_SIZE[index];
    handleFontChange(newSize);
  };

  const handleMouseEnter = () => {
    setHover(true);
  };

  const handleMouseLeave = () => {
    setHover(false);
  };

  const handleColorChange = (color) => {
    setColor(color);

    // setting null will delete the property
    commitAppearence("bgcolor", color === "#ffffff" ? null : color);
  };

  return (
    <TableRow
      hover={true}
      sx={{ ...sx, backgroundColor: color }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <TableCell
        component="th"
        scope="row"
        align="left"
        size="small"
        sx={{
          wordBreak: "break-word",
          whiteSpace: "normal",
          pb: 0,
          pt: 0.3,
          pr: 0,
          minHeight: "18px",
        }}
      >
        <Stack
          direction={"row"}
          alignItems={"flex-start"}
          justifyContent={"space-between"}
        >
          <Stack direction={"column"} sx={{ width: "100%" }}>
            <MemoMessage
              item={item}
              storyKey={nodebug ? storyKey : ""}
              overflow={overflow}
              fontSize={fontSize}
            />

            {(expand || rawJson) && (
              <Typography
                sx={{ color: "text.secondary", fontSize: 10 }}
                component={"span"}
              >
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "keep-all",
                  }}
                >
                  {getMessageJson()}
                </pre>
              </Typography>
            )}
          </Stack>
          <Box sx={{ minWidth: 55, height: 18 }}>
            {hover && (
              <Stack direction={"row"} justifyContent="flex-end" gap={0}>
                {!nodebug && !expand && (
                  <IconButton size="small" onClick={() => toggleRawJson()}>
                    <CommentIcon fontSize="inherit" sx={{ color: "silver" }} />
                  </IconButton>
                )}
                {!nodebug && (
                  <Tooltip title="Copy JSON to clipboard">
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(getMessageJson());
                      }}
                    >
                      <ContentCopyIcon
                        fontSize="inherit"
                        sx={{ color: "silver" }}
                      />
                    </IconButton>
                  </Tooltip>
                )}

                {!nodebug &&
                  !allowReplay &&
                  "st_hide" in item &&
                  item.st_hide && (
                    <Tooltip title="Restore message">
                      <IconButton
                        size="small"
                        onClick={() => handleRestoreMessage(item)}
                      >
                        <RestoreFromTrashIcon
                          fontSize="inherit"
                          sx={{ color: "silver" }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}

                {allowReplay && (
                  <Tooltip title="Replay event">
                    <IconButton size="small" onClick={() => handleReplay(item)}>
                      <RepeatIcon fontSize="inherit" sx={{ color: "silver" }} />
                    </IconButton>
                  </Tooltip>
                )}

                {allowReplay && (
                  <Tooltip title="Copy link to clipboard">
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          window.location.protocol +
                            "//" +
                            window.location.host +
                            "/message/" +
                            activeProject +
                            "/" +
                            item.SK +
                            "/" +
                            window.location.search
                        );
                      }}
                    >
                      <LinkIcon fontSize="inherit" sx={{ color: "silver" }} />
                    </IconButton>
                  </Tooltip>
                )}

                {!allowReplay && item.st_auto_joined && (
                  <Tooltip title="Disjoin automatically linked message">
                    <IconButton
                      size="small"
                      onClick={() => {
                        handleDisjoinMessage(item);
                      }}
                    >
                      <CallSplitIcon
                        sx={{ color: "silver", fontSize: ICON_FONT_SIZE }}
                      />
                    </IconButton>
                  </Tooltip>
                )}

                {!item.dummy &&
                  !allowReplay &&
                  disjoin.has(`${item.PK}#${item.SK}`) && (
                    <Tooltip title="Disjoin thread">
                      <IconButton
                        sx={{ color: "silver" }}
                        size="small"
                        onClick={() => {
                          handleDisjoinThread(item);
                        }}
                      >
                        <CallSplitIcon
                          sx={{ color: "silver", fontSize: ICON_FONT_SIZE }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                {!item.dummy && (
                  <>
                    <ColorPicker
                      color={color}
                      onChange={handleColorChange}
                      fontSize={fontSize}
                      onChangeFontSize={handleFontChange}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleCloseMessage(item)}
                    >
                      <DeleteOutlineIcon
                        sx={{ color: "silver", fontSize: ICON_FONT_SIZE }}
                      />
                    </IconButton>
                  </>
                )}
              </Stack>
            )}
          </Box>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export function Thread({
  messages,
  setMessages,
  allowReplay,
  expand,
  story,
  background,
  overflow,
}) {
  const [disjoin, setDisjoin] = useState(new Set());
  const nodebug = useRecoilValue(debugToggleState);
  const storyKey = story && "SK4" in story ? `${Number(story.SK4)}` : "";

  const getRowStyle = (item) => {
    if (item.st_ui && item.st_ui.notif) {
      for (let notif_date of item.st_ui.notif) {
        const date = u.parsePythonTimestamp(notif_date);
        if (u.isTodayDate(date)) {
          return { background: "#f5eeee" };
        }
      }
    }

    return {};
  };

  useEffect(() => {
    let disjoin = new Set();
    let visited = new Set();
    if (messages && messages.length > 0) {
      const first = messages[0];
      const last = messages[messages.length - 1];
      const fwd = first.SK < last.SK;

      let starter = first.SK < last.SK ? first.PK : last.PK;
      if (story) {
        starter = story.PK.split("#")[0] + "#" + story.SK;
      }

      for (let i = 0; i < messages.length; ++i) {
        const index = fwd ? i : messages.length - 1 - i;

        const pk = messages[index].PK;
        const sk = messages[index].SK;

        if (pk !== starter && (!visited.has(pk) || messages[index].st_edit)) {
          disjoin.add(`${pk}#${sk}`);
          visited.add(pk);
        }
      }
    }
    setDisjoin(disjoin);
  }, [messages]);

  let filteredMessages = [];
  if (messages && messages.length) {
    if (!allowReplay && nodebug) {
      let edited = {};

      // group edits by the message they apply to
      for (let m of messages) {
        let edit_ts = m.st_edit;
        if (m.subtype === "message_changed") {
          edit_ts = m.message.ts;
        }

        if (edit_ts) {
          if (!(edit_ts in edited)) {
            edited[edit_ts] = [m.SK];
          } else {
            edited[edit_ts].push(m.SK);
          }
        }
      }

      // remove all edits except the most recent one
      let filterOut = new Set();
      for (let [k, v] of Object.entries(edited)) {
        filterOut.add(k);
        const mostRecentEdit = v[0] > v[v.length - 1] ? v[0] : v[v.length - 1];
        for (let m of v) {
          if (m !== mostRecentEdit) {
            filterOut.add(m);
          }
        }
      }

      // remove deleted messages
      for (let m of messages) {
        if (m.st_hide) {
          filterOut.add(m.SK);
        }
      }

      // remove title message unless it was edited in which case
      // we'll have the most recent edit present in the thread
      if (
        story &&
        "st_created_at" in story &&
        !(story.st_created_at in edited)
      ) {
        const ts = story.st_created_at;
        filterOut.add(story.st_created_at);
        if (ts in edited) {
          for (let e of edited[ts]) {
            filterOut.add(e);
          }
        }
      }

      filteredMessages = messages.filter((item) => {
        return !filterOut.has(item.SK);
      });
    } else {
      filteredMessages = messages;
    }
  }

  return (
    <TableContainer
      sx={{
        background: background || "none",
        borderRadius: "6px",
        border: "5px solid #f3f3f3",
      }}
    >
      <Table size="small" aria-label="simple table">
        <TableBody>
          {filteredMessages &&
            filteredMessages.map(function (item, index) {
              return (
                <ThreadRow
                  key={
                    item.old_dummy_message ? item.old_dummy_message.SK : item.SK
                  }
                  item={item}
                  nodebug={nodebug}
                  overflow={overflow}
                  expand={expand}
                  storyKey={storyKey}
                  disjoin={disjoin}
                  setMessages={setMessages}
                  story={story}
                  allowReplay={allowReplay}
                  sx={{
                    // "&:last-child td, &:last-child th": { border: 0 },
                    ...(filteredMessages.length > 1 ? getRowStyle(item) : {}),
                    borderBottom: "solid white",
                  }}
                />
              );
            })}
          <TableRow>
            <TableCell sx={{ height: "15px" }} />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function Message({
  item,
  storyKey,
  overflow,
  fontSize = DEFAULT_FONT_SIZE,
}) {
  return (
    <Stack direction="row" alignItems="flex-start" gap={1}>
      {isGithub(item) && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            exitToApp(item);
          }}
        >
          <Icon icon={"uil:github"} fontSize={fontSize * 1.35} />
        </IconButton>
      )}
      {isGitlab(item) && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            exitToApp(item);
          }}
        >
          <Icon icon={"uil:gitlab"} fontSize={fontSize * 1.35} />
        </IconButton>
      )}
      <Box width="100%">
        <Authorship item={item} />
        <MessagePreview
          item={item}
          storyKey={storyKey}
          overflow={overflow}
          fontSize={fontSize}
        />
      </Box>
    </Stack>
  );
}

function Authorship({ item }) {
  return (
    <Typography sx={{ color: "silver", fontSize: 6, lineHeight: 1 }}>
      <span>
        <UserName userId={getUser(item)} />
      </span>
      <span>{` - ${getTimestamp(item)}`}</span>
    </Typography>
  );
}

export const exitToApp = (item) => {
  if ("channel" in item || item.st_source === "slack") {
    // slack
    const request = async (myWindow) => {
      try {
        const story = item.SK;
        let params = { exittoapp: true, story: story };
        if (item.channel) {
          params.channel = item.channel;
        }
        const response = await http_post("stories", params);
        myWindow.location.href = response.data.exittoapp.url;
      } catch (err) {
      } finally {
      }
    };
    if ("permalink" in item) {
      window.open(item.permalink, "_blank", "noreferrer");
    } else {
      const myWindow = window.open();
      request(myWindow);
    }
  } else {
    let url = getSourceUrl(item);

    if (url) {
      window.open(url, "_blank", "noreferrer");
    }
  }
};

export const MemoMessage = memo(Message);
